import { Bell, Check, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useNotifications } from "@/hooks/useNotifications";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";

interface NotificationBadgeProps {
  isTransparent?: boolean;
}

export function NotificationBadge({ isTransparent: _ }: NotificationBadgeProps) {
  const { unreadCount } = useNotifications();

  if (unreadCount === 0) return null;

  return (
    <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
      {unreadCount > 99 ? "99+" : unreadCount}
    </span>
  );
}

export function NotificationPanel() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();

  const handleClick = (notif: { id: string; link: string | null; read: boolean }) => {
    if (!notif.read) markAsRead(notif.id);
    if (notif.link) navigate(notif.link);
  };

  return (
    <div>
      <div className="flex items-center justify-between px-4 py-3">
        <h3 className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Notifications
        </h3>
        {unreadCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); markAllAsRead(); }}
            className="h-auto gap-1 px-2 py-1 text-[10px] text-muted-foreground hover:text-foreground"
          >
            <CheckCheck className="h-3 w-3" /> Mark all read
          </Button>
        )}
      </div>
      <ScrollArea className="max-h-[240px]">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <Bell className="mb-1.5 h-5 w-5 text-muted-foreground/40" />
            <p className="font-sans text-xs text-muted-foreground">No notifications</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {notifications.slice(0, 5).map((notif) => (
              <button
                key={notif.id}
                onClick={(e) => { e.stopPropagation(); handleClick(notif); }}
                className={cn(
                  "flex w-full gap-2.5 px-4 py-2.5 text-left transition-colors hover:bg-secondary/50",
                  !notif.read && "bg-primary/5"
                )}
              >
                <div className="mt-1.5 flex-shrink-0">
                  {!notif.read ? (
                    <span className="block h-1.5 w-1.5 rounded-full bg-primary" />
                  ) : (
                    <Check className="h-3 w-3 text-muted-foreground/40" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={cn(
                    "font-sans text-xs",
                    !notif.read ? "font-medium text-foreground" : "text-muted-foreground"
                  )}>
                    {notif.title}
                  </p>
                  {notif.body && (
                    <p className="mt-0.5 line-clamp-1 font-sans text-[10px] text-muted-foreground">
                      {notif.body}
                    </p>
                  )}
                  <p className="mt-0.5 font-sans text-[10px] text-muted-foreground/60">
                    {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
